import { Link } from "react-router-dom";
import { trackClick } from "@/lib/analytics";

const AUDIENCES = [
  {
    key: "families",
    eyebrow: "For families",
    title: "Make the road part of the trip.",
    body: "Two-queen rooms and a three-bedroom family suite, kids under 18 stay free in existing beds, the free hot breakfast keeps the picky eaters happy — and Brunswick's World's Largest Pecan is twenty-five minutes north.",
    cta: { label: "Plan a family stay", to: "/blog/worlds-largest-pecan-brunswick-missouri" },
    image: "/images/property/king-room-front.jpg",
  },
  {
    key: "business",
    eyebrow: "For crews & business travel",
    title: "Built for the working week.",
    body: "Utility crews, line workers, wind-farm contractors, sales reps — we run direct billing, hold blocks of rooms, and keep coffee hot before sunrise. Wi-Fi works for actual work; the bed works after.",
    cta: { label: "Set up direct billing", to: "/contact" },
    image: "/images/property/king-room-wide.jpg",
  },
  {
    key: "truckers",
    eyebrow: "For drivers",
    title: "A real stop on US-65.",
    body: "Easy in, easy out — paved truck and bus parking, full LED lighting, 24-hour front desk, and a trucker rate you'll only get by mentioning you drive. Hot food next door at any hour.",
    cta: { label: "Trucker's guide to US-65", to: "/blog/trucker-parking-overnight-us-65-missouri" },
    image: "/images/property/exterior-main.jpg",
  },
  {
    key: "weekend",
    eyebrow: "For weekend wanderers",
    title: "Quiet corners of Missouri.",
    body: "Swan Lake National Wildlife Refuge, Van Meter State Park, Baltimore Bend Vineyard, the village of Arrow Rock. Hunting parties take the three-bedroom suite; everyone else takes their time. Carrollton is the basecamp — close to everything quiet.",
    cta: { label: "Read the local guide", to: "/local-guide" },
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Swan_Lake_shore_and_open_water_-_DPLA_-_a598e73b17825cb501299d4e4899ba92.jpg/1280px-Swan_Lake_shore_and_open_water_-_DPLA_-_a598e73b17825cb501299d4e4899ba92.jpg",
  },
];

export default function AudienceCards() {
  return (
    <section className="bg-canvas py-24 md:py-32" data-testid="audience-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7">
            <p className="font-mono-label text-ember mb-4">Built for the way you travel</p>
            <h2 className="font-display text-5xl md:text-6xl text-ink leading-[1.02]">
              Whoever you are<br />
              <span className="italic">on US-65 today.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:text-right">
            <p className="text-ink-soft leading-relaxed max-w-md md:ml-auto">
              Westbridge isn&apos;t trying to be everything to everyone. We know who&apos;s pulling into the
              lot and we&apos;ve built the place around the four kinds of people who keep coming back.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-rule-strong">
          {AUDIENCES.map((a, i) => (
            <article
              key={a.key}
              className="bg-canvas group fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`audience-${a.key}`}
            >
              <div className="aspect-[16/10] overflow-hidden bg-dune">
                <img
                  src={a.image}
                  alt={a.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]"
                  loading="lazy"
                />
              </div>
              <div className="p-8 md:p-12">
                <p className="font-mono-label text-ember mb-4">{a.eyebrow}</p>
                <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">{a.title}</h3>
                <p className="mt-5 text-ink-soft leading-relaxed text-lg">{a.body}</p>
                <Link
                  to={a.cta.to}
                  onClick={() => trackClick(`audience:${a.key}`)}
                  className="mt-6 inline-block link-underline font-mono-label text-ink hover:text-ember"
                  data-testid={`audience-cta-${a.key}`}
                >
                  {a.cta.label} →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
